/**
 * Google Analytics 4 tracking with consent-aware event dispatch.
 * Events only fire after the user has granted analytics consent.
 */

type GtagCommand = 'config' | 'event' | 'js' | 'consent' | 'set';
type GtagFunction = (
  command: GtagCommand,
  targetOrDate: string | Date,
  params?: Record<string, unknown>
) => void;

// Extend window for gtag/dataLayer access
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: GtagFunction;
  }
}

const ENV_MEASUREMENT_ID = import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY as
  | string
  | undefined;

// The gtag.js snippet lives in index.html so it loads before the app bundle.
// This module only pushes events onto the queue it created.
const INLINE_MEASUREMENT_ID = 'G-GRNJCHV7RR';
const MEASUREMENT_ID = ENV_MEASUREMENT_ID || INLINE_MEASUREMENT_ID;

const SCRIPT_SRC = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;

// Google Ads conversion tracking.
// AW ID + conversion label. The label is created in Google Ads under
// Tools & Settings > Conversions > (your website conversion action).
// Replace the label below with the real one from Google Ads.
export const GOOGLE_ADS_CONVERSION_ID = 'AW-18252346676';
export const GOOGLE_ADS_CONVERSION_LABEL = 'x3leCOWSteYcELTqsv9D';

const hasConsent = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem('analytics_consent') === 'granted';
  } catch {
    return false;
  }
};

const ensureGtag = () => {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];

  if (!window.gtag) {
    window.gtag = function (...args: unknown[]) {
      window.dataLayer.push(args);
    };
  }

  // Fallback: only load/configure gtag if the inline snippet is missing
  // (e.g. a prerendered shell without the head script).
  if (document.querySelector(`script[src="${SCRIPT_SRC}"]`)) return;

  const script = document.createElement('script');
  script.async = true;
  script.src = SCRIPT_SRC;
  document.head.appendChild(script);

  window.gtag('js', new Date());
  window.gtag('config', MEASUREMENT_ID, { send_page_view: false });
};

const updateConsentState = (granted: boolean) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  const state = granted ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    analytics_storage: state,
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
  });
};

// Core Web Vitals tracking
export const trackWebVitals = (metric: {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
}) => {
  ensureGtag();
  if (!hasConsent() || !MEASUREMENT_ID) return;

  window.gtag?.('event', 'web_vital', {
    event_category: 'Web Vitals',
    event_label: metric.name,
    value: Math.round(metric.value),
    custom_parameter_1: metric.rating,
  });
};

// Page view tracking
export const trackPageView = (path: string, title?: string) => {
  ensureGtag();
  if (!hasConsent() || !MEASUREMENT_ID) return;

  const pageTitle = title || (typeof document !== 'undefined' ? document.title : '');
  const pageLocation = typeof window !== 'undefined' ? window.location.href : '';

  window.gtag?.('event', 'page_view', {
    page_path: path,
    page_title: pageTitle,
    page_location: pageLocation,
  });
};

// Generic event tracking
export const trackEvent = (
  eventName: string,
  eventData?: Record<string, unknown>
) => {
  ensureGtag();
  if (!hasConsent() || !MEASUREMENT_ID) return;

  window.gtag?.('event', eventName, eventData);
};

/**
 * Fire a Google Ads conversion. Used on the /bedankt page after a successful
 * contact-form submission. Only fires when consent has been granted and a real
 * conversion label has been set.
 */
export const trackGoogleAdsConversion = (transactionId?: string) => {
  ensureGtag();
  if (!hasConsent()) return;
  if (!GOOGLE_ADS_CONVERSION_LABEL) {
    // No label configured yet — log so it's visible during setup.
    console.warn('[analytics] Google Ads conversion label not configured yet.');
    return;
  }

  window.gtag?.('event', 'conversion', {
    send_to: `${GOOGLE_ADS_CONVERSION_ID}/${GOOGLE_ADS_CONVERSION_LABEL}`,
    value: 1.0,
    currency: 'EUR',
    transaction_id: transactionId || `lead-${Date.now()}`,
  });
};

// Predefined event types for consistency
export const AnalyticsEvents = {
  // CTA events
  CTA_CLICK: 'cta_click',
  BOOK_NOW: 'book_now_click',
  CONTACT_SUBMIT: 'contact_form_submit',

  // Engagement events
  CHAT_OPENED: 'chat_opened',
  CHAT_MESSAGE_SENT: 'chat_message_sent',
  VIDEO_PLAYED: 'video_played',

  // Navigation events
  GAME_DETAIL_VIEW: 'game_detail_viewed',
  GENRE_FILTER: 'genre_filter_applied',

  // Form events
  FORM_START: 'form_started',
  FORM_SUBMIT: 'form_submitted',
  FORM_ERROR: 'form_error'
} as const;

// Set consent status
export const setAnalyticsConsent = (granted: boolean) => {
  if (typeof window === 'undefined') return;
  localStorage.setItem('analytics_consent', granted ? 'granted' : 'denied');
  updateConsentState(granted);
};

// Check consent status
export const getAnalyticsConsent = (): boolean => {
  return hasConsent();
};

// Initialize analytics (loads gtag script so it is ready when consent is granted)
export const initAnalytics = () => {
  ensureGtag();
  updateConsentState(hasConsent());
};
